import { defineMeta } from '@/registry/types';

export const buttonMeta = defineMeta({
  name: 'Button',
  level: 'atom',
  category: 'input',
  classification: 'mui-overrides',
  description:
    'Primary interactive control for actions. Themed MUI Button with an emphasis ladder of variants: `primary` (one per view), `secondary` (default), `ghost`, and `destructive`.',
  baseLibrary: '@mui/material',
  muiEquivalent: 'Button',
  muiDocs: 'https://mui.com/material-ui/react-button/',
  a2uiEquivalent: 'Button',
  importPath: '@/atoms/Button',
  requiredProps: [],
  optionalProps: ['variant', 'size', 'startIcon', 'endIcon', 'disabled', 'onClick'],
  props: [
    {
      name: 'variant',
      type: "'primary' | 'secondary' | 'ghost' | 'destructive'",
      default: 'secondary',
    },
    { name: 'size', type: "'small' | 'medium' | 'large'", default: 'medium' },
    { name: 'disabled', type: 'boolean', default: 'false' },
    { name: 'onClick', type: '(e: MouseEvent) => void' },
  ],
  customVariants: ['primary', 'secondary', 'ghost', 'destructive'],
  supportedStates: ['default', 'hover', 'focus', 'active', 'disabled'],
  designTokens: ['colors', 'radius', 'typography', 'controlHeights'],
  accessibility: [
    'Renders a native <button>; keyboard operable (Enter/Space) by default.',
    'Provide descriptive text or aria-label for icon-only usage (prefer IconButton).',
    'Focus ring is visible via Mui-focusVisible.',
  ],
  usageExamples: [
    '<Button variant="primary">Save changes</Button>',
    '<Button variant="destructive">Delete account</Button>',
  ],
  rules: [
    'Every action control is the design-system Button; never a raw <button>, a directly-imported MUI Button, or a custom-styled clickable element.',
    'Pick the variant from the emphasis ladder: `primary` for the single most important action in a view, `secondary` (the default) for ordinary actions, `ghost` for tertiary ones such as Cancel, `destructive` for irreversible ones.',
    'When several buttons sit inline, make them all `secondary` and promote at most one to `primary`. If you cannot confidently judge which should be primary, ask.',
    'Write labels that name the action ("Save changes", "Send invite"), not "OK" or "Click here".',
    'Show a loading state for actions that take time, so the user knows it is working.',
    'For a dedicated icon-only control use IconButton with an accessible label and a tooltip, not Button.',
    'Pick `size` from context: `small` in dense toolbars and tables, `medium` in standard forms, `large` for prominent CTAs. If you are not sure, ask.',
  ],
  antiPatterns: [
    'Do not place more than one primary button in the same view; it dilutes the visual hierarchy.',
    'Do not use the destructive variant without a confirmation step for irreversible actions.',
    'Do not use Button for navigation between pages; use a link.',
    'Do not put long sentences inside a Button.',
  ],
  llmSafe: true,
  whenToUse: ['Triggering an action', 'Submitting a form', 'Confirming a dialog'],
  whenNotToUse: ['Navigating to another page (use a link)', 'Toggling a setting (use Switch/ToggleButton)'],
});

export default buttonMeta;
